import type {
	FilterRuleModel,
	Metric,
	MetricModel,
	PricingEngineType,
	StatisticsInterpretabilityResponse,
	StrategyDataUnit,
} from '@/shared/generated'
import type { MetricsDataUnit } from '../model/types'

import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { formatDistanceToNow } from 'date-fns'
import { AnimatePresence, motion } from 'framer-motion'
import { any, filter, map, matches, pipe, prop, sum } from 'lodash/fp'
import { AlertTriangleIcon, SearchXIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { CurrencyPicker } from '@/features/currency-picker'

import { useMetricsQuery } from '@/entities/bi'
import {
	CurrencyScopeProvider,
	useCurrenciesQuery,
	useSelectedCurrency,
} from '@/entities/currency'
import { useOptimizationQuery } from '@/entities/optimization'
import {
	MetricGroup,
	MetricGroupValue,
	MetricName,
	useInterpretabilityStatisticQuery,
	useRepricingStatisticQuery,
} from '@/entities/optimization-statistic'
import { PricingAlertsWidget } from '@/entities/price-alert'
import { usePricingCampaignsQuery } from '@/entities/pricing-campaign'
import { useScenariosQuery } from '@/entities/scenario'

import { AiScenariosRecommendation } from '@/widgets/optimization-summary/ai-scenarios-recommendation'
import { SalesFactorsImpactWidget } from '@/widgets/sales-factors-impact'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { Operators } from '@/shared/components/query-builder'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '@/shared/components/ui/accordion'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { DeltaValue } from '@/shared/components/ui/delta-value'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import { Label } from '@/shared/components/ui/label'
import { Content, Layout } from '@/shared/components/ui/layout'
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/shared/components/ui/sheet'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Spinner } from '@/shared/components/ui/spinner'
import { Switch } from '@/shared/components/ui/switch'
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from '@/shared/components/ui/tabs'
import { OptimizationStatus } from '@/shared/generated'
import { useOptimizationStatus } from '@/shared/hooks/data'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { ampli } from '@/shared/lib/ampli'
import { formatNumber } from '@/shared/tools/locale'
import { getFiltersQueryString } from '@/shared/tools/location'

import {
	formatMericValue,
	formatTotalDiffValue,
	getMetricCurrency,
	getMetricDiffByType,
	getPrefixedMetric,
	getTranslationKey,
	getTypesByMetricSchema,
	METRIC_PREFIX,
	METRICS_WIDGET_TYPES,
	PREDICT_METRICS,
} from '../lib/helpers'
import AverageValuesWidget from './AverageValuesWidget'
import ElasticityDistributionWidget from './ElasticityDistributionWidget'
import ItemsChangedWidget from './ItemsChangedWidget'
import { OpenStateProvider } from './OpenStateProvider'
import OptimizationMetricsWidget from './OptimizationMetricsWidget'
import PricingLinesChangeTypeWidget from './PricingLinesChangeTypeWidget'
import ProgressWidgets from './ProgressWidgets'
import PromoWidget from './PromoWidget'

export function OptimizationSummaryDrawer({
	isOpen,
	close,
}: {
	isOpen: boolean
	close: () => void
}) {
	const optimizationGroupId = useOptimizationGroupId()
	const status = useOptimizationStatus(optimizationGroupId!)
	const open =
		[OptimizationStatus.Finished, OptimizationStatus.Locked].includes(
			status!
		) && isOpen

	return (
		<Sheet
			modal={false}
			defaultOpen={open}
			open={open}
			onOpenChange={(open) => !open && close()}
		>
			<SheetContent
				onPointerDownOutside={(e) => e.preventDefault()}
				onInteractOutside={(e) => e.preventDefault()}
				disbleOverlay
				className='inset-y-2 right-2 h-auto w-[600px] gap-0 rounded-3xl border bg-background/80 backdrop-blur-xl sm:max-w-[600px]'
			>
				<ErrorBoundary>
					<OpenStateProvider>
						<CurrencyScopeProvider>
							<OptimizationSummary />
						</CurrencyScopeProvider>
					</OpenStateProvider>
				</ErrorBoundary>
			</SheetContent>
		</Sheet>
	)
}
function OptimizationSummary() {
	const { t } = useTranslation()

	const [shouldUseQueryFilters, setShouldUseQueryFilters] = useState(false)
	const optimizationGroupId = useOptimizationGroupId()
	const [{ filters: queryFilters }] = useLocationQuery()
	const filters = useMemo(
		() => (shouldUseQueryFilters ? queryFilters : []),
		[shouldUseQueryFilters, queryFilters]
	)
	const optimizationQuery = useOptimizationQuery(optimizationGroupId!)
	useEffect(() => {
		!!optimizationQuery.data && ampli.optSummaryView()
	}, [optimizationQuery.data])
	const scenariosQuery = useScenariosQuery(optimizationGroupId!, {
		select: (data) => ({
			mainScenarioId: data.find(({ is_main }) => is_main)?.id,
			recommendedScenarios: filter({ scenario_type: 'recommendation' }, data),
		}),
		enabled: !!optimizationGroupId,
	})
	const pricingCampaignEnginesQuery = usePricingCampaignsQuery<
		PricingEngineType[]
	>(
		{
			optimization_group_id: optimizationGroupId,
			scenario_id: scenariosQuery.data?.mainScenarioId,
		},
		{
			select: (data) => data.map(prop('engine')) as PricingEngineType[],
			enabled: !!scenariosQuery.data?.mainScenarioId,
		}
	)
	const isRuleBasedGroup = useMemo(
		() => pricingCampaignEnginesQuery?.data?.every((engine) => engine === 'RB'),
		[pricingCampaignEnginesQuery.data]
	)
	const currenciesQuery = useCurrenciesQuery()
	// Hold content until the currency is known to avoid a double fetch.
	const isLoading =
		currenciesQuery.isPending ||
		any(prop('isLoading'), [
			optimizationQuery,
			scenariosQuery,
			pricingCampaignEnginesQuery,
		])
	if (isLoading) {
		return (
			<Layout className='flex h-full items-center justify-center overflow-y-hidden'>
				<Empty>
					<EmptyHeader>
						<EmptyMedia variant='icon'>
							<Spinner />
						</EmptyMedia>
						<EmptyTitle>
							{t('general_loading', { defaultValue: 'Loading...' })}
						</EmptyTitle>
						<EmptyDescription>
							{t('general_loading_description', {
								defaultValue: 'Please wait while we load the data...',
							})}
						</EmptyDescription>
					</EmptyHeader>
				</Empty>
			</Layout>
		)
	}

	return (
		<Layout className='h-full overflow-y-hidden'>
			<SheetHeader>
				<SheetTitle className='text-base'>
					{t('opt.summary', { defaultValue: 'Optimization summary' })}
				</SheetTitle>
				{!!optimizationQuery.data!.finished_date && (
					<SheetDescription>
						{t('status_panel_repricing_finished')}:{' '}
						{formatDistanceToNow(
							new Date(optimizationQuery.data!.finished_date + 'Z'),
							{
								addSuffix: true,
							}
						)}
					</SheetDescription>
				)}
				<TargetMericsSummary filters={filters} />
			</SheetHeader>
			{!!scenariosQuery.data?.recommendedScenarios?.length && (
				<AiScenariosRecommendation
					count={scenariosQuery.data.recommendedScenarios.length}
				/>
			)}
			<Tabs defaultValue='statistics' className='overflow-hidden'>
				<div className='mx-4 flex items-center justify-between'>
					<TabsList>
						<TabsTrigger value='statistics' data-tab='statistics'>
							{t('app.statistics', { defaultValue: 'Statistics' })}
						</TabsTrigger>
						<TabsTrigger value='optimization' data-tab='optimization'>
							{t('app.optimization', { defaultValue: 'Optimization' })}
						</TabsTrigger>
						<TabsTrigger value='progress' data-tab='progress'>
							{t('app.progress', { defaultValue: 'Progress' })}
						</TabsTrigger>
					</TabsList>
					<div className='flex items-center gap-2'>
						<CurrencyPicker />
						<Switch
							checked={shouldUseQueryFilters}
							onCheckedChange={(checked) => {
								ampli.optSummaryUseTableFiltersSwitch({
									value: String(checked),
								})
								setShouldUseQueryFilters(checked)
							}}
						/>
						<Label className='mx-2 flex items-center'>
							{t('opt.summary.use_table_filters', {
								defaultValue: 'Use table filter',
							})}
						</Label>
					</div>
				</div>
				<Content className='relative overflow-y-auto'>
					<AnimatePresence mode='wait'>
						<TabsContent value='statistics' asChild>
							<TabContent key='statistics'>
								<StatisticsTab filters={filters} />
							</TabContent>
						</TabsContent>
						<TabsContent value='optimization' asChild>
							<TabContent key='optimization'>
								<OptimizationTab filters={filters} />
							</TabContent>
						</TabsContent>
						<TabsContent value='progress' asChild>
							<TabContent key='progress'>
								<ErrorBoundary>
									{isRuleBasedGroup ? (
										<TabIsNotAvailable />
									) : (
										<ProgressWidgets filters={filters} />
									)}
								</ErrorBoundary>
							</TabContent>
						</TabsContent>
					</AnimatePresence>
				</Content>
			</Tabs>
		</Layout>
	)
}
function StatisticsTab({ filters }: { filters: FilterRuleModel[] }) {
	const { t } = useTranslation()
	const currency = useSelectedCurrency()

	useEffect(() => {
		ampli.optSummaryOpenStatisticsTab()
	}, [])
	const navigate = useNavigate()
	const optimizationGroupId = useOptimizationGroupId()!
	const optimizationQuery = useOptimizationQuery(optimizationGroupId)
	const repricingStatisticQuery = useRepricingStatisticQuery<MetricModel[]>(
		{
			optimizations: [
				{
					optimization_id: optimizationQuery.data!.id,
					optimization_group_id: optimizationGroupId,
				},
			],
			filters,
		},
		{
			enabled: !!optimizationGroupId && !!optimizationQuery.data?.id,
			refetchOnMount: true,
			staleTime: 0,
			select: (data) =>
				data?.metrics.filter(
					matches({
						group_type: MetricGroup.PriceChangeStatus,
						name: MetricName.ProductCount,
					})
				) || [],
		}
	)
	const changed = formatNumber(
		sum(
			map(
				prop('value'),
				filter(
					({ group_value }) =>
						[MetricGroupValue.Increased, MetricGroupValue.Decreased].includes(
							group_value as MetricGroupValue
						),
					repricingStatisticQuery.data as MetricModel[]
				)
			)
		) || 0
	)
	const queryParams = useMemo(
		() => ({
			optimizations: [
				{
					optimization_id: optimizationQuery.data!.id,
					optimization_group_id: optimizationGroupId,
				},
			],
			filters,
		}),
		[optimizationQuery.data, optimizationGroupId, filters]
	)
	const isLoading = any(prop('isLoading'), [
		optimizationQuery,
		repricingStatisticQuery,
	])
	const total = formatNumber(optimizationQuery.data?.total_products || 0)
	const handleNavigate = ({
		widget,
		widgetFilter,
	}: {
		widget: string
		widgetFilter: FilterRuleModel[]
	}) => {
		ampli.optSummaryPromoWidgetClickRow({
			filters: widgetFilter.map((f) => f.name),
		})
		navigate({ to: getFiltersQueryString([...filters, ...widgetFilter]) })
	}

	return (
		<div className='divide-base flex w-full flex-col divide-y divide-solid pb-14'>
			<ErrorBoundary>
				<Accordion
					defaultValue={['alerts', 'skus', 'pls', 'promo', 'avg']}
					type='multiple'
					className='w-full'
				>
					<AccordionItem value='alerts'>
						<AccordionTrigger>{t('alerts')}</AccordionTrigger>
						<AccordionContent>
							<PricingAlertsWidget currency={currency} />
						</AccordionContent>
					</AccordionItem>
					<AccordionItem value='skus'>
						<AccordionTrigger>
							<div className='grid gap-1'>
								<h5 className='text-base'>
									{t('skus.changed', { defaultValue: 'SKUs changed' })}
								</h5>
								<p className='text-sm font-normal text-muted-foreground'>
									{t('skus.changed.subtitle', {
										changed,
										total,
										defaultValue: `${changed} of ${total} SKUs were optimized`,
									})}
								</p>
							</div>
						</AccordionTrigger>
						<AccordionContent>
							<ItemsChangedWidget filters={filters} />
						</AccordionContent>
					</AccordionItem>
					<AccordionItem value='pls'>
						<AccordionTrigger>
							{t('pls.change_types', {
								defaultValue: 'Pricing lines by change types',
							})}
						</AccordionTrigger>
						<AccordionContent>
							<PricingLinesChangeTypeWidget
								queryParams={queryParams}
								isLoading={isLoading}
							/>
						</AccordionContent>
					</AccordionItem>
					<AccordionItem value='promo'>
						<AccordionTrigger>{t('overview_price_promo')}</AccordionTrigger>
						<AccordionContent>
							<PromoWidget
								handleNavigate={handleNavigate}
								queryParams={queryParams}
								isLoading={isLoading}
							/>
						</AccordionContent>
					</AccordionItem>
					<AccordionItem value='avg'>
						<AccordionTrigger>
							{t('average_price', { defaultValue: 'Average shelf price' })}
						</AccordionTrigger>
						<AccordionContent>
							<AverageValuesWidget
								queryParams={queryParams}
								isLoading={isLoading}
							/>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</ErrorBoundary>
		</div>
	)
}
function OptimizationTab({ filters }: { filters: FilterRuleModel[] }) {
	const { t } = useTranslation()

	useEffect(() => {
		ampli.optSummaryOpenOptimizationTab()
	}, [])
	const metricsQuery = useMetricsQuery({
		select: filter(({ type }) => PREDICT_METRICS.includes(type)),
	})
	const optimizationGroupId = useOptimizationGroupId()!
	const salesFactorsImpactFilters = useMemo(
		() => [
			...filters,
			{
				name: 'optimization_group_id',
				operation: Operators.IS as const,
				value: optimizationGroupId,
			},
		],
		[filters, optimizationGroupId]
	)
	const optimizationQuery = useOptimizationQuery(optimizationGroupId)
	const currency = useSelectedCurrency()
	const interpretabilityStatistic = useInterpretabilityStatisticQuery(
		{
			optimizations: [
				{
					optimization_group_id: optimizationGroupId,
					optimization_id: optimizationQuery.data!.id,
				},
			],
			filters,
			currency,
		},
		{
			enabled: !!optimizationQuery.data?.id,
			refetchOnMount: true,
			staleTime: 0,
		}
	)
	if (interpretabilityStatistic.error?.response?.status === 404) {
		return <TabIsNotAvailable />
	}
	if (interpretabilityStatistic.error?.response?.status === 500) {
		return (
			<Empty>
				<EmptyHeader>
					<EmptyMedia variant='icon'>
						<AlertTriangleIcon />
					</EmptyMedia>
					<EmptyTitle>{t('fatal_error_title')}</EmptyTitle>
					<EmptyDescription>{t('fatal_error_desc')}</EmptyDescription>
				</EmptyHeader>
			</Empty>
		)
	}
	if (
		interpretabilityStatistic.isFetched &&
		!interpretabilityStatistic.data &&
		metricsQuery.isFetched
	) {
		return <TabIsNotAvailable />
	}
	const hasCustomMetrics = !!metricsQuery.data?.filter(
		({ type }) => type === 'predict_custom'
	).length

	return (
		<div className='divide-base flex w-full flex-col divide-y divide-solid pb-14'>
			<ErrorBoundary>
				<Accordion
					defaultValue={[
						'targeted',
						'forecast',
						'custom',
						'distribution',
						'salesFactorsImpact',
					]}
					type='multiple'
					className='w-full'
				>
					<AccordionItem value='targeted'>
						<AccordionTrigger>{t('status_panel_targeted')}</AccordionTrigger>
						<AccordionContent>
							<OptimizationMetricsWidget
								widgetType={METRICS_WIDGET_TYPES.TARGET}
								filters={filters}
								metricsSchema={metricsQuery.data!}
							/>
						</AccordionContent>
					</AccordionItem>
					<AccordionItem value='forecast'>
						<AccordionTrigger>{t('status_panel_forecast')}</AccordionTrigger>
						<AccordionContent>
							<OptimizationMetricsWidget
								widgetType={METRICS_WIDGET_TYPES.ADDITIONAL}
								filters={filters}
								metricsSchema={metricsQuery.data!}
							/>
						</AccordionContent>
					</AccordionItem>
					{hasCustomMetrics && (
						<AccordionItem value='custom'>
							<AccordionTrigger>
								{t('status_panel_custom', { defaultValue: 'Custom metrics' })}
							</AccordionTrigger>
							<AccordionContent>
								<OptimizationMetricsWidget
									widgetType={METRICS_WIDGET_TYPES.CUSTOM}
									filters={filters}
									metricsSchema={metricsQuery.data!}
								/>
							</AccordionContent>
						</AccordionItem>
					)}
					<AccordionItem value='distribution'>
						<AccordionTrigger>
							<div className='grid gap-1'>
								<h5 className='text-base'>
									{t('elasticity.distribution', {
										defaultValue: 'Distribution of elasticity',
									})}
								</h5>
								<p className='text-sm font-normal text-muted-foreground'>
									{t('elasticity.distribution.subtitle', {
										defaultValue:
											'Click on tab to see different additional insights.',
									})}
								</p>
							</div>
						</AccordionTrigger>
						<AccordionContent>
							<ElasticityDistributionWidget filters={filters} />
						</AccordionContent>
					</AccordionItem>
					<AccordionItem value='salesFactorsImpact'>
						<AccordionTrigger>
							<div className='grid gap-1'>
								<h5 className='text-base'>
									{t('widget.top_sales_factors.title', {
										defaultValue: 'Top sales factors',
									})}
								</h5>
								<p className='text-sm font-normal text-muted-foreground'>
									{t('widget.top_sales_factors.subtitle', {
										defaultValue:
											'This chart shows how different factors affect your sales in available key metrics.',
									})}
								</p>
							</div>
						</AccordionTrigger>
						<AccordionContent>
							<SalesFactorsImpactWidget
								filters={salesFactorsImpactFilters ?? []}
							/>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</ErrorBoundary>
		</div>
	)
}

function TargetMericsSummary({ filters }: { filters?: FilterRuleModel[] }) {
	const { t } = useTranslation()

	const optimizationGroupId = useOptimizationGroupId()!
	const optimizationQuery = useOptimizationQuery(optimizationGroupId)
	const currency = useSelectedCurrency()
	const metricsQuery = useMetricsQuery({
		select: pipe([filter(({ type }) => type === 'predict_default')]),
	})
	const repricingStatisticQuery = useRepricingStatisticQuery<StrategyDataUnit>(
		{
			optimizations: [
				{
					optimization_id: optimizationQuery.data!.id,
					optimization_group_id: optimizationGroupId,
				},
			],
			filters: [],
		},
		{
			enabled: !!optimizationGroupId && !!optimizationQuery.data?.id,
			refetchOnMount: true,
			staleTime: 0,
			select: prop('settings.strategy'),
		}
	)
	const interpretabilityStatistic = useInterpretabilityStatisticQuery(
		{
			optimizations: [
				{
					optimization_group_id: optimizationGroupId,
					optimization_id: optimizationQuery.data!.id,
				},
			],
			filters,
			currency,
		},
		{
			refetchOnMount: true,
			staleTime: 0,
			enabled:
				!!optimizationQuery.data?.id &&
				!!metricsQuery.data &&
				!!repricingStatisticQuery.data,
			select: (data) => {
				const { target, protect } = getMainMetricsNames(
					repricingStatisticQuery.data as StrategyDataUnit
				)
				const mainMetricValues = extractMetricsValues(
					(data as StatisticsInterpretabilityResponse)
						?.metrics as MetricsDataStatistic,
					{ target, protect }
				)

				return {
					target: getMetricValues({
						key: target,
						data: mainMetricValues.target,
						metricsSchema: metricsQuery.data!,
						currency,
					}),
					protect: getMetricValues({
						key: protect,
						data: mainMetricValues.protect,
						metricsSchema: metricsQuery.data!,
						currency,
					}),
				}
			},
		}
	)
	if (interpretabilityStatistic.isLoading) {
		return (
			<div className='mt-2 flex w-full space-x-4'>
				<Skeleton className='h-10 w-24' />
				<Skeleton className='h-10 w-20' />
			</div>
		)
	}
	if (interpretabilityStatistic.isError) {
		return (
			<Alert variant='destructive' className='mr-5'>
				<AlertTriangleIcon />
				<AlertDescription>
					{interpretabilityStatistic.error?.message || t('fatal_error_title')}
				</AlertDescription>
			</Alert>
		)
	}
	const data = interpretabilityStatistic.data

	return (
		<div className='mt-2 flex gap-6'>
			{data?.target && (
				<DescriptionGroup>
					<DescriptionTerm>
						{`${t(getTranslationKey(data?.target.key))} (${t('to_grow', { defaultValue: 'to grow' })})`}
					</DescriptionTerm>
					<DescriptionDetail>
						<span className='text-lg font-semibold text-foreground'>
							{data?.target.value}
						</span>
						<DeltaValue className='ml-1 text-sm' value={data?.target.diffValue}>
							{formatTotalDiffValue(
								data?.target.diffValue,
								data?.target.diffType,
								t,
								data?.target.currency
							)}
						</DeltaValue>
					</DescriptionDetail>
				</DescriptionGroup>
			)}
			{data?.protect && (
				<DescriptionGroup>
					<DescriptionTerm>
						{`${t(getTranslationKey(data?.protect.key))} (${t('to_maintain', { defaultValue: 'to maintain' })})`}
					</DescriptionTerm>
					<DescriptionDetail>
						<span className='text-lg font-semibold text-foreground'>
							{data?.protect.value}
						</span>
						<DeltaValue
							className='ml-1 text-sm'
							value={data?.protect.diffValue}
						>
							{formatTotalDiffValue(
								data?.protect.diffValue,
								data?.protect.diffType,
								t,
								data?.protect.currency
							)}
						</DeltaValue>
					</DescriptionDetail>
				</DescriptionGroup>
			)}
		</div>
	)
}
function TabContent({ children }: { children: React.ReactNode }) {
	return (
		<motion.div
			initial={{ opacity: 0, x: 100 }}
			animate={{ opacity: 1, x: 0 }}
			exit={{ opacity: 0, x: -30 }}
			className='divide-base flex size-full flex-col divide-y divide-solid px-4'
			transition={{
				duration: 0.2,
			}}
		>
			{children}
		</motion.div>
	)
}

function TabIsNotAvailable() {
	const { t } = useTranslation()

	return (
		<Empty>
			<EmptyHeader>
				<EmptyMedia variant='icon'>
					<SearchXIcon />
				</EmptyMedia>
				<EmptyDescription>
					{t('opt.summary.not_available_for_rb_description', {
						defaultValue: 'This tab is not available for rule-based groups.',
					})}
				</EmptyDescription>
			</EmptyHeader>
		</Empty>
	)
}
const getMetricValues = ({
	key,
	data,
	metricsSchema,
	currency,
}: {
	key: string
	data: MetricsDataUnit
	metricsSchema: Metric[]
	currency?: string
}) => {
	const metricSchema = metricsSchema?.find(
		({ name }) => name.split('.')[0] === `${key}`
	)
	if (!metricSchema) {
		return
	}
	const { diffType, dataType } = getTypesByMetricSchema(metricSchema!)
	const diffValue = getMetricDiffByType(data, diffType)
	const metricCurrency = getMetricCurrency(metricSchema, currency)

	return {
		key,
		value: formatMericValue({
			value: data.final || 0,
			type: dataType,
			currency: metricCurrency,
		}),
		diffType,
		diffValue,
		currency: metricCurrency,
	}
}
type MetricsDataStatistic = Record<string, number>
const extractMetricsValues = (
	data: MetricsDataStatistic,
	mainMetrics: { target: string; protect: string }
) => {
	const { target, protect } = mainMetrics

	return {
		target: {
			init: data?.[`${target}.init`],
			final: data?.[`${target}.final`],
			optimal: data?.[`${target}.optimal`],
		},
		protect: {
			init: data?.[`${protect}.init`],
			final: data?.[`${protect}.final`],
			optimal: data?.[`${protect}.optimal`],
		},
	}
}
const getMainMetricsNames = (statistics: StrategyDataUnit) => ({
	target: getPrefixedMetric({
		metric: statistics?.target as string,
		prefix: METRIC_PREFIX,
	}),
	protect: getPrefixedMetric({
		metric: statistics?.protect as string,
		prefix: METRIC_PREFIX,
	}),
})
